import { Outlet } from 'react-router-dom'

import { WorkScheduleRootPageDocument } from 'generated/resources/graphql'

import AuthorizeUser from 'modules/core/components/AuthorizeUser'
import type { Back } from 'modules/core/components/Card/types'
import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import dayConfigurations, {
  DayConfigurationsProvider,
} from 'modules/work_schedules/store/collections/dayConfigurations'
import overlapPeriods from 'modules/work_schedules/store/collections/overlapPeriods'
import schedules from 'modules/work_schedules/store/collections/schedules'

const WorkScheduleRootPage = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()

  const { data, loading: loadingGraphql } = useQuery(WorkScheduleRootPageDocument, {})

  const back: Back = {
    href: navigation.settings.workSchedule.fullPath(),
    label: i18n.t('settings.time_management.tab'),
  }

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        dayConfigurations.fetch({ data: { schedule_id: String(id) } }),
        overlapPeriods.fetch({ data: { schedule_id: id } }),
        dayConfigurations.fetch({ data: { schedule_id: id } }),
      ]),
    [id]
  )

  const workSchedule = schedules?.get(String(id))
  const workScheduleId = workSchedule?.attributes.id
  const defaultPeriod = workScheduleId ? overlapPeriods.getDefaultPeriod(workScheduleId) : undefined

  if (loading || loadingGraphql) return <Loading />
  if (!workSchedule) return <NotFound />

  return (
    <AuthorizeUser
      authorizeFn={() => data?.workSchedule?._policies?.manageCompanySchedules?.can ?? false}
    >
      <DayConfigurationsProvider collection={dayConfigurations}>
        <Outlet
          context={{
            isNotFound: !workSchedule.id,
            back,
            workSchedule,
            overlapPeriods,
            defaultPeriod,
            dayConfigurations,
            loading,
          }}
        />
      </DayConfigurationsProvider>
    </AuthorizeUser>
  )
}

export default WorkScheduleRootPage
